import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { cn } from './ui/Button';
import supabase from '../supabase/supabase';

export default function NotificationBell({ light = false }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);
  const userIdRef = useRef(null);

  const fetchNotifications = async (uid) => {
    if (!uid) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('notifications_1740423033529')
      .select('*')
      .eq('user_id', uid)
      .order('created_at', { ascending: false })
      .limit(20);
    if (!error && data) {
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !mounted) return;

      setUserId(user.id);
      userIdRef.current = user.id;
      await fetchNotifications(user.id);

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      channelRef.current = supabase
        .channel(`notif-bell-${user.id}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications_1740423033529',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            if (!mounted) return;
            setNotifications(prev => [payload.new, ...prev].slice(0, 20));
            setUnreadCount(prev => prev + 1);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications_1740423033529',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            if (!mounted) return;
            setNotifications(prev =>
              prev.map(n => n.id === payload.new.id ? payload.new : n)
            );
            setUnreadCount(prev =>
              payload.new.is_read ? Math.max(0, prev - 1) : prev
            );
          }
        )
        .subscribe((status) => {
          console.log('[NotificationBell] Realtime status:', status);
        });
    };

    init();

    return () => {
      mounted = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  const markAllAsRead = async () => {
    if (!userId) return;
    await supabase
      .from('notifications_1740423033529')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const markAsRead = async (id) => {
    const { error } = await supabase
      .from('notifications_1740423033529')
      .update({ is_read: true })
      .eq('id', id);
    if (!error) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.is_read) await markAsRead(n.id);
    if (n.action_link) {
      setIsOpen(false);
      navigate(n.action_link);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const getNotifIcon = (title) => {
    if (!title) return 'Bell';
    const t = title.toLowerCase();
    if (t.includes('confirm')) return 'CheckCircle';
    if (t.includes('cancel')) return 'XCircle';
    if (t.includes('complet')) return 'Award';
    if (t.includes('payment')) return 'DollarSign';
    if (t.includes('booking') || t.includes('request')) return 'Calendar';
    return 'Bell';
  };

  const getNotifColor = (title) => {
    if (!title) return 'bg-gray-100 text-gray-500';
    const t = title.toLowerCase();
    if (t.includes('confirm')) return 'bg-blue-50 text-blue-600';
    if (t.includes('cancel')) return 'bg-red-50 text-red-500';
    if (t.includes('complet')) return 'bg-green-50 text-green-600';
    if (t.includes('payment')) return 'bg-purple-50 text-purple-600';
    return 'bg-brand-primary/10 text-brand-primary';
  };

  return (
    <div className="relative">
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && userId) fetchNotifications(userId);
        }}
        className={cn(
          "relative w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-95 border",
          light
            ? "bg-white border-gray-200 text-brand-dark hover:bg-gray-50"
            : "bg-white/5 border-white/10 text-white hover:bg-white/10"
        )}
      >
        <SafeIcon name="Bell" className="text-lg" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center animate-pulse">
            <span className="text-[9px] font-black text-white leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
            {/* Header */}
            <div className="p-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-brand-dark text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="text-[9px] bg-red-500 text-white px-2 py-0.5 rounded-full font-black">
                    {unreadCount} NEW
                  </span>
                )}
              </div>
              <button
                onClick={() => fetchNotifications(userId)}
                className="text-gray-400 hover:text-brand-primary transition-colors p-1"
                title="Refresh">
                <SafeIcon name="RefreshCw" className="text-sm" />
              </button>
            </div>

            {/* List */}
            <div className="max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-bold">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <SafeIcon name="BellOff" className="text-2xl mx-auto mb-2 opacity-40" />
                  <p className="text-xs font-bold uppercase tracking-widest">All caught up!</p>
                  <p className="text-[10px] text-gray-300 mt-1">No notifications yet</p>
                </div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n.id}
                    className={cn(
                      "p-4 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors",
                      !n.is_read ? "bg-blue-50/30" : ""
                    )}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5", getNotifColor(n.title))}>
                        <SafeIcon name={getNotifIcon(n.title)} className="text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p className={cn(
                            "text-sm leading-tight",
                            !n.is_read ? "font-black text-brand-dark" : "font-bold text-gray-700"
                          )}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <span className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{n.message}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-gray-400 font-medium">{timeAgo(n.created_at)}</span>
                          {n.action_link && (
                            <span className="text-[10px] font-black uppercase tracking-widest text-brand-primary flex items-center gap-0.5">
                              View <SafeIcon name="ChevronRight" className="text-[10px]" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="w-full py-3 text-xs font-bold text-brand-primary hover:bg-gray-50 transition-colors border-t border-gray-50">
                Mark all as read
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}